import { useState, useEffect } from 'react';

import {
	Package,
	Eye,
	CheckCircle,
	Truck,
	Clock,
	AlertCircle,
	XCircle,
	ChevronLeft,
	ChevronRight,
	ArrowUpCircle,
	Inbox,
	CircleDot,
	BadgeDollarSign,
	BadgeCheck,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import CustomerLayout from '@/components/layouts/CustomerLayout';
import type { Shipment } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { shipmentService } from '@/lib/database';

export default function CustomerShipments() {
	const location = useLocation();
	const [shipments, setShipments] = useState<Shipment[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [isSearchMode, setIsSearchMode] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	const navigate = useNavigate();

	// Mock customer ID - in real app, this would come from auth
	const customerId = localStorage.getItem('userId');

	useEffect(() => {
		// Check if we came from a search
		if (location.state?.searchQuery) {
			setSearchQuery(location.state.searchQuery);
			setIsSearchMode(true);
			if (location.state.searchResults) {
				setShipments(location.state.searchResults);
				setLoading(false);
			} else {
				setShipments([]);
				setLoading(false);
			}
		} else {
			loadShipments();
		}
	}, [location.state]);

	const loadShipments = async () => {
		try {
			setLoading(true);
			const data = await shipmentService.getByCustomerId(customerId!, 50);
			setShipments(data);
		} catch (error) {
			console.error('Error loading shipments:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = async () => {
		if (!searchQuery.trim()) {
			setIsSearchMode(false);
			loadShipments();
			return;
		}

		setLoading(true);
		setIsSearchMode(true);
		try {
			const results = await shipmentService.search(searchQuery.trim());
			setShipments(results);
		} catch (error) {
			console.error('Error searching shipments:', error);
			setShipments([]);
		} finally {
			setLoading(false);
		}
	};

	const clearSearch = () => {
		setSearchQuery('');
		setIsSearchMode(false);
		loadShipments();
	};

	const calculateCubicFeet = (dimensions?: string): string => {
		if (!dimensions) return '0';

		const parts = dimensions.split('x').map(d => parseFloat(d.trim()));
		if (parts.length !== 3 || parts.some(isNaN)) return '0';

		const [length, width, height] = parts;
		const cubicFeet = (length * width * height) / 1728; // Convert cubic inches to cubic feet
		return cubicFeet.toFixed(2);
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case 'picked_up_by_recipient':
			case 'delivered_to_recipient':
				return <CheckCircle className="h-4 w-4 text-green-500" />;

			case 'in_transit_to_country':
			case 'in_transit_to_recipient':
				return <Truck className="h-4 w-4 text-blue-500" />;

			case 'ready_for_pickup':
				return <Package className="h-4 w-4 text-purple-500" />;

			case 'arrived_at_warehouse':
				return <AlertCircle className="h-4 w-4 text-orange-500" />;

			case 'processing_at_warehouse':
				return <Clock className="h-4 w-4 text-yellow-500" />;

			case 'picking_up_from_sender':
				return <ArrowUpCircle className="h-4 w-4 text-teal-500" />;

			case 'received_at_warehouse':
				return <Inbox className="h-4 w-4 text-sky-500" />;

			case 'created':
				return <CircleDot className="h-4 w-4 text-gray-500" />;

			case 'estimated':
				return <BadgeDollarSign className="h-4 w-4 text-amber-500" />;

			case 'confirmed':
				return <BadgeCheck className="h-4 w-4 text-green-600" />;

			case 'cancelled':
			case 'refunded':
				return <XCircle className="h-4 w-4 text-red-500" />;

			default:
				return <XCircle className="h-4 w-4 text-gray-500" />;
		}
	};

	const getStatusBadge = (status: string) => {
		const variants = {
			picked_up_by_recipient: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
			delivered_to_recipient: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
			in_transit_to_country: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
			in_transit_to_recipient: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
			ready_for_pickup: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
			arrived_at_warehouse: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200',
			processing_at_warehouse: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200',
			picking_up_from_sender: 'bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200',
			received_at_warehouse: 'bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200',
			created: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200',
			estimated: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200',
			confirmed: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
			cancelled: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
			refunded: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
		};

		return (
			<Badge className={variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'}>
				{status.replace(new RegExp('_', 'g'), ' ').toUpperCase()}
			</Badge>
		);
	};

	const getTotalItems = (shipment: Shipment) => {
		return shipment.packages?.reduce((total, pkg) => total + (pkg.items?.length || 0), 0) || 0;
	};

	const filteredShipments = shipments.filter(shipment =>
		shipment.tracking_number.toLowerCase().includes(searchQuery.toLowerCase()));

	const totalPages = Math.ceil(filteredShipments.length / itemsPerPage);
	const paginatedShipments = filteredShipments.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	return (
		<CustomerLayout>
			<div className="space-y-6">
				{/* Header */}
				<div>
					<h1 className="text-3xl font-bold text-gray-900">My Shipments</h1>
					<p className="text-gray-600">View and track all your shipments</p>
				</div>

				{/* Search */}
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>
									{isSearchMode ? `Search Results (${filteredShipments.length})` : `All Shipments (${filteredShipments.length})`}
								</CardTitle>
								<CardDescription>
									{isSearchMode ? `Results for "${searchQuery}"` : 'Complete history of your shipments with tracking'}
								</CardDescription>
							</div>
							{/* <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by tracking number..."
                    className="pl-10 w-64"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button onClick={handleSearch} disabled={loading}>
                  Search
                </Button>
                {isSearchMode && (
                  <Button variant="outline" onClick={clearSearch}>
                    Clear
                  </Button>
                )}
              </div> */}
						</div>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{paginatedShipments.map((shipment) => (
								<div
									key={shipment.id}
									className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 shadow-sm transition-colors"
								>
									<div className="flex items-center space-x-4">
										<div className="flex items-center space-x-2">
											{getStatusIcon(shipment.status!)}
											<div>
												<p className="font-medium text-gray-900">{shipment.tracking_number}</p>

												<p className="text-sm text-gray-600 capitalize">
													{shipment.shipping_method} Shipping
												</p>

												<p className="text-sm text-gray-600">
													{shipment.route === 'us_to_ht' ? 'USA to Haiti' : 'Haiti to USA'}
												</p>
											</div>
										</div>
									</div>

									<div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
										<div className="flex justify-between space-x-6">
											{shipment.shipping_method === 'air' && (
												<div className="text-center">
													<p className="text-sm font-medium text-gray-900">Total Weight</p>
													<p className="text-xs text-gray-600">{shipment.total_weight} lbs</p>
												</div>
											)}
											<div className="text-right md:text-center">
												<p className="text-sm font-medium text-gray-900">Items</p>
												<p className="text-xs text-gray-600">{getTotalItems(shipment)}</p>
											</div>
										</div>

										<div className="flex mt-2 md:mt-0 justify-between space-x-6">
											<div className="md:text-center">
												<p className="text-sm font-medium text-gray-900">Cost</p>
												<p className="text-xs text-gray-600">${shipment.total_cost}</p>
											</div>

											<div className="text-right md:text-center">
												<p className="text-sm font-medium text-gray-900">Date Created</p>
												<p className="text-xs text-gray-600">
													{new Date(shipment.created_at!).toLocaleDateString()}
												</p>
											</div>
										</div>


										<div className="flex justify-center my-4 md:my-0">
											<div className="flex text-center space-x-2">
												{getStatusBadge(shipment.status!)}
											</div>
										</div>

										<Button
											size="sm"
											// onClick={() => handleTrackShipment(shipment)}
											onClick={() => {
												navigate(`/shipments/${shipment.tracking_number}`);
											}}
										>
											<Eye className="h-4 w-4 mr-1" />
											Track
										</Button>
									</div>
								</div>
							))}

							{filteredShipments.length === 0 && (
								<div className="text-center py-12">
									<Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
									<h3 className="text-lg font-medium text-gray-900 mb-2">No shipments found</h3>
									<p className="text-gray-600">
										{isSearchMode ? `No shipments found for "${searchQuery}". Try a different tracking number.` : 'You haven\'t created any shipments yet.'}
									</p>
								</div>
							)}
						</div>

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="flex items-center justify-between mt-6">
								<p className="text-sm text-gray-600">
									Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredShipments.length)} of {filteredShipments.length} shipments
								</p>
								<div className="flex items-center space-x-2">
									<Button
										variant="outline"
										size="sm"
										onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
										disabled={currentPage === 1}
									>
										<ChevronLeft className="h-4 w-4" />
										Previous
									</Button>
									<span className="text-sm text-gray-600">
										Page {currentPage} of {totalPages}
									</span>
									<Button
										variant="outline"
										size="sm"
										onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
										disabled={currentPage === totalPages}
									>
										Next
										<ChevronRight className="h-4 w-4" />
									</Button>
								</div>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</CustomerLayout>
	);
}